import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** Two colourway pickers + an Add to cart button for product cards (buy 1 get 1 free). */
class RumeCardBuy extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('mouseover', this.#onHover);
    this.addEventListener('focusin', this.#onHover);
    this.addEventListener('mouseleave', this.#restoreAll, true);
    document.addEventListener('click', this.#onOutside);
    this.querySelectorAll('[data-select]').forEach((select) => {
      let closeTimer;
      select.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      select.addEventListener('mouseleave', () => {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => select.removeAttribute('open'), 180);
      });
      const first = select.querySelector('[data-value]');
      select.querySelectorAll('[data-option]').forEach((o) => {
        if (o.dataset.variant === first.value) {
          o.classList.add('is-selected');
          select.dataset.image = o.dataset.image;
          select.dataset.label = o.dataset.label;
        }
      });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseover', this.#onHover);
    this.removeEventListener('focusin', this.#onHover);
    this.removeEventListener('mouseleave', this.#restoreAll, true);
    document.removeEventListener('click', this.#onOutside);
  }

  /** @param {MouseEvent} event */
  #onOutside = (event) => {
    if (this.contains(/** @type {Node} */ (event.target))) return;
    this.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
  };

  /** Show the hovered swatch's photo + name in the pill. @param {Event} event */
  #onHover = (event) => {
    const option = /** @type {HTMLElement} */ (event.target).closest?.('[data-option]');
    if (!option) return;
    const select = option.closest('[data-select]');
    select.querySelector('.rcb__thumb').src = option.dataset.image;
    select.querySelector('[data-name]').textContent = option.dataset.label;
  };

  /** @param {Element} select */
  #restore(select) {
    if (select.dataset.image) select.querySelector('.rcb__thumb').src = select.dataset.image;
    if (select.dataset.label) select.querySelector('[data-name]').textContent = select.dataset.label;
  }

  #restoreAll = (event) => {
    const select = /** @type {HTMLElement} */ (event.target).closest?.('[data-select]');
    if (select) this.#restore(select);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // Keep clicks here from reaching the product card, which would navigate to the product page.
    event.stopPropagation();

    const target = /** @type {HTMLElement} */ (event.target);
    const option = target.closest('[data-option]');
    if (option instanceof HTMLButtonElement && !option.disabled) {
      const select = option.closest('[data-select]');
      select.querySelector('[data-value]').value = option.dataset.variant;
      select.dataset.image = option.dataset.image;
      select.dataset.label = option.dataset.label;
      select.querySelectorAll('[data-option]').forEach((o) => o.classList.toggle('is-selected', o === option));
      this.#restore(select);
      select.removeAttribute('open');
      return;
    }

    const summary = target.closest('[data-select] > summary');
    if (summary) {
      const own = summary.parentElement;
      this.querySelectorAll('details[open]').forEach((d) => d !== own && d.removeAttribute('open'));
      return;
    }

    if (target.closest('[data-add]')) this.#add();
  };

  async #add() {
    const button = /** @type {HTMLButtonElement} */ (this.querySelector('[data-add]'));
    const label = this.querySelector('[data-add-label]');
    if (button.disabled) return;

    const counts = new Map();
    this.querySelectorAll('[data-value]').forEach((input) => {
      const id = input.value;
      if (id) counts.set(id, (counts.get(id) || 0) + 1);
    });
    const items = [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));
    if (!items.length) return;

    button.disabled = true;
    const original = label.textContent;
    const total = items.reduce((sum, item) => sum + item.quantity, 0);

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());

      const cart = await fetch(`${Theme.routes.cart_url}.json`).then((r) => r.json());
      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'rume-card-buy', itemCount: total },
        });
        label.textContent = 'Unavailable';
      } else {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'rume-card-buy',
            itemCount: total,
            sections: response.sections,
            didError: false,
          },
        });
        label.textContent = 'Added \u2713';
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      label.textContent = 'Try again';
    }

    setTimeout(() => {
      label.textContent = original;
      button.disabled = false;
    }, 1400);
  }
}

if (!customElements.get('rume-card-buy')) customElements.define('rume-card-buy', RumeCardBuy);
